import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CreateSlideWizard } from "./CreateSlideWizard";
import { GenerationProgress } from "./GenerationProgress";
import { useCreateSlideGenerator } from "../hooks/useCreateSlideGenerator";
import { canUseDialog, openCreateSlidesDialog } from "../hooks/openCreateSlidesDialog";
import { insertSlideFromBase64 } from "../hooks/slideInsert";
import { insertSlideFromModel } from "../hooks/insertSlideModel";
import { createApiGenerator } from "../service/apiGenerator";
import { readThemeColors } from "../hooks/themeColors";
import {
  buildPalette,
  intentToCategory,
  slideModelFromOutline,
  type SlidePalette,
} from "../hooks/slideModel";
import {
  slidePptxBase64,
  type SlideBrief,
  type SlideXmlResult,
  type ThemeColors,
} from "../service/slideGenerationApi";
import { DEFAULT_CONFIG, type CreateSlideConfig } from "../hooks/types";
import { ModuleHeader } from "../../../../../shared/components/ModuleHeader";
import { useDeckorateHttp } from "../../../hooks/useDeckorateHttp";
import { config as appConfig } from "../../../../../shared/config/environment";
import styles from "./CreateSlide.module.css";

interface CreateSlideTabProps {
  readonly onBack?: () => void;
}

/**
 * "Create slides" tab. The wizard (source → details → design → review) opens in
 * an Office Dialog modal over PowerPoint; on "Generate" the config comes back to
 * the pane, where generation + slide insertion run so the user watches progress
 * live. When the Dialog API isn't available (browser/tests) the wizard falls
 * back to an inline pane flow. When `onBack` is provided a Deckorate header is
 * shown so the user can return to the module landing (matches FormatTools).
 */
export function CreateSlideTab({ onBack }: Readonly<CreateSlideTabProps>) {
  const body = canUseDialog() ? <CreateSlideLauncher onBack={onBack} /> : <CreateSlideInline />;
  if (!onBack) return body;
  return (
    <div className={styles.wrap}>
      <ModuleHeader title="Create Slides" subtitle="Deckorate" onBack={onBack} />
      {body}
    </div>
  );
}

/**
 * Inserts one finished slide into the active deck. Prefers the backend's
 * designed base64 `.pptx` (verbatim); otherwise renders the structured model
 * built from the outline (chevrons/tables/etc.) so the slide is still designed
 * rather than a plain bullet list.
 */
function insertGeneratedSlide(
  result: SlideXmlResult,
  brief: SlideBrief | undefined,
  palette: SlidePalette,
): Promise<void> {
  const pptx = slidePptxBase64(result);
  // Diagnostic: shows whether the API returned a designed slide or just an
  // outline for this slide, so terse content can be traced to its source.
  let slideSource: string;
  if (pptx) {
    slideSource = "designed PPTX";
  } else if (result.xml) {
    slideSource = "OOXML (not rendered)";
  } else {
    slideSource = "outline points only";
  }
  console.info(
    `[create-slide] "${result.title}" → ${slideSource} · ${brief?.main_points?.length ?? 0} points`,
  );
  if (pptx) return insertSlideFromBase64(pptx);
  const category = intentToCategory(brief?.intent_tag ?? result.intent_tag);
  const model = slideModelFromOutline(
    brief?.title ?? result.title,
    brief?.main_points ?? [],
    category,
    palette,
  );
  return insertSlideFromModel(model);
}

/**
 * Builds the pane-side generator: streams from the DeckorateAPI and inserts each
 * slide into the active deck. Null until the user is signed in (no token).
 */
function usePaneGenerator() {
  // Shared authorised client (bearer + APIM key + 401 refresh + telemetry),
  // bound to the Deckorate backend (config.deckorateApiBaseUrl) — same flow as
  // every other Deckorate feature hook.
  const http = useDeckorateHttp();

  // Read the deck's theme colours (pane-only host API) so generated slides
  // match the active presentation's brand. Degrades to undefined on failure.
  const [themeColors, setThemeColors] = useState<ThemeColors | undefined>();
  useEffect(() => {
    let cancelled = false;
    readThemeColors()
      .then((t) => {
        if (!cancelled && t) setThemeColors(t);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  // Colour palette derived from the deck's theme, so model-rendered slides
  // (the no-PPTX fallback) match the deck's brand.
  const palette = useMemo(() => buildPalette(themeColors), [themeColors]);

  const generator = useMemo(
    () =>
      createApiGenerator({
        baseUrl: appConfig.deckorateApiBaseUrl,
        authorizedFetch: http.fetch,
        themeColors,
        insertSlide: (result, brief) => insertGeneratedSlide(result, brief, palette),
      }),
    [http, themeColors, palette],
  );

  return useCreateSlideGenerator(generator);
}

/* ── Host path: wizard in the dialog modal, generation in the pane ── */

interface CreateSlideLauncherProps {
  readonly onBack?: () => void;
}

function CreateSlideLauncher({ onBack }: Readonly<CreateSlideLauncherProps>) {
  const gen = usePaneGenerator();
  const [topic, setTopic] = useState("");
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const launch = useCallback(async () => {
    setOpening(true);
    setError(null);
    try {
      const cfg = await openCreateSlidesDialog();
      if (cfg) {
        setTopic(cfg.topic || cfg.documents[0]?.name || "your deck");
        gen.start(cfg);
      } else {
        // The user cancelled/closed the wizard without configuring anything —
        // there's no button left on this tab to relaunch it, so return to the
        // Deckorate home instead of stranding the user on a dead idle screen.
        onBack?.();
      }
    } catch (e) {
      // Genuine failure to open the dialog — log the detail and show the user a
      // clear, actionable message instead of silently doing nothing.
      console.error("[create-slide] failed to open the Create Slides wizard:", e);
      setError(
        e instanceof Error
          ? e.message
          : "Couldn't open the Create Slides window. Please try again.",
      );
    } finally {
      setOpening(false);
    }
  }, [gen, onBack]);

  // Opening the tab opens the wizard dialog immediately — no separate
  // "Create slides" click needed. Guarded so it fires exactly once per
  // mount even under React StrictMode's double-invoke.
  const startedRef = useRef(false);
  useEffect(() => {
    if (!startedRef.current) {
      startedRef.current = true;
      launch().catch(() => undefined);
    }
  }, []);

  if (gen.phase !== "idle") {
    return (
      <GenerationProgress
        topic={topic}
        current={gen.current}
        total={gen.total}
        plans={gen.plans}
        phase={gen.phase}
        error={gen.error}
        onCancel={gen.cancel}
        onRestart={gen.reset}
      />
    );
  }

  // Nothing to show while the dialog is opening/open — the wizard launches
  // immediately on mount with no intermediate idle screen. Only a genuine
  // failure to open the dialog gets a visible message + retry button.
  if (!error) return <div className={styles.launch} />;

  return (
    <div className={styles.launch}>
      <p className={styles.error} role="alert">
        {error}
      </p>
      <button
        type="button"
        className={styles.btnPrimary}
        onClick={() => {
          launch().catch(() => undefined);
        }}
        disabled={opening}
      >
        {opening ? "Opening…" : "Retry"}
      </button>
    </div>
  );
}

/* ── Fallback: inline wizard (no Dialog API — browser/tests) ── */

function CreateSlideInline() {
  const gen = usePaneGenerator();
  const [config, setConfig] = useState<CreateSlideConfig>(DEFAULT_CONFIG);

  if (gen.phase !== "idle") {
    return (
      <GenerationProgress
        topic={config.topic}
        current={gen.current}
        total={gen.total}
        plans={gen.plans}
        phase={gen.phase}
        error={gen.error}
        onCancel={gen.cancel}
        onRestart={gen.reset}
      />
    );
  }

  return (
    <CreateSlideWizard
      config={config}
      onChange={(patch) => setConfig((c) => ({ ...c, ...patch }))}
      onGenerate={() => gen.start(config)}
    />
  );
}
